import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DataApi from '../api/dataApi';
import ConfirmationModal from '../components/common/ConfirmationModal';

// ── Status Config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  PENDING:   { label: 'Pending',   bg: '#FEF7E0', color: '#8A6400', dot: '#F9AB00' },
  FULFILLED: { label: 'Fulfilled', bg: '#E6F4EA', color: '#137333', dot: '#34A853' },
  CANCELLED: { label: 'Cancelled', bg: '#FCE8E6', color: '#C5221F', dot: '#EA4335' },
};

// ── Icons (inline SVG to avoid dependencies) ─────────────────────────────────
const Icon = ({ d, size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d={d} fill={color} />
  </svg>
);
const ICONS = {
  add:     "M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z",
  refresh: "M17.65 6.35A7.958 7.958 0 0 0 12 4C7.58 4 4.01 7.58 4.01 12S7.58 20 12 20c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z",
  search:  "M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
  mail:    "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z",
  edit:    "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z",
  trash:   "M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z",
  book:    "M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z",
  close:   "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
  check:   "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
  info:    "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z",
  warn:    "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z",
  pending: "M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm.5 5v5.25l4.5 2.67-.75 1.23L11 13V7h1.5z",
};

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, iconPath, primaryColor, surfaceColor }) => (
  <div style={{
    background: surfaceColor,
    borderRadius: 5,
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,.08), 0 1px 2px rgba(0,0,0,.06)',
    transition: 'box-shadow .2s cubic-bezier(.2,0,0,1)',
    cursor: 'default',
  }}
  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.12)'}
  onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.08), 0 1px 2px rgba(0,0,0,.06)'}
  >
    <div style={{
      width: 48, height: 48, borderRadius: 16,
      background: primaryColor + '22',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <Icon d={iconPath} size={24} color={primaryColor} />
    </div>
    <div>
      <div style={{ fontSize: 12, fontFamily: "'Google Sans Text', sans-serif", fontWeight: 500, color: '#5F6368', letterSpacing: '.4px', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 28, fontFamily: "'Google Sans Display', sans-serif", fontWeight: 700, color: '#202124', lineHeight: 1 }}>{value}</div>
    </div>
  </div>
);

// ── Status Chip ───────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: cfg.bg, color: cfg.color,
      borderRadius: 20, padding: '4px 12px 4px 8px',
      fontSize: 12, fontFamily: "'Google Sans Text', sans-serif", fontWeight: 600,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

// ── Icon Button ───────────────────────────────────────────────────────────────
const IconBtn = ({ iconPath, label, onClick, disabled, tint = '#1A73E8' }) => (
  <button
    title={label}
    disabled={disabled}
    onClick={onClick}
    style={{
      width: 40, height: 40, borderRadius: 20, border: 'none', cursor: disabled ? 'default' : 'pointer',
      background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background .15s', opacity: disabled ? 0.35 : 1, color: tint,
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = tint + '14' }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
  >
    <Icon d={iconPath} size={18} color={tint} />
  </button>
);

// ── Filled Button ─────────────────────────────────────────────────────────────
const FilledBtn = ({ children, onClick, variant = 'filled', icon }) => {
  const styles = {
    filled: { bg: '#11439b', color: '#fff', border: 'none', hoverBg: '#11439b' },
    tonal:  { bg: '#E8F0FE', color: '#1A73E8', border: 'none', },
    outlined: { bg: 'transparent', color: '#1A73E8', border: '1.5px solid #DADCE0',  },
  };
  const s = styles[variant];
  return (
    <button
      onClick={onClick}
    //   style={{background:"var(--primary-color)", color:"#fff"}}
      style={{
        height: 40, padding: '0 6px', borderRadius: 6,
        background:"var(--primary-color)", color:"#fff",
        fontFamily: "'Google Sans Text', sans-serif", fontWeight: 600, fontSize: 14,
        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
        transition: 'background .15s, box-shadow .15s',
        border: 'none',
      }}
    //   onMouseEnter={e => { e.currentTarget.style.background = s.hoverBg }}
    //   onMouseLeave={e => { e.currentTarget.style.background = s.bg }}
    >
      {icon && <Icon d={icon} size={18} color={s.color} />}
      {children}
    </button>
  );
};

// ── Modal Shell ───────────────────────────────────────────────────────────────
const Dialog = ({ open, onClose, title, children, actions }) => {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(32,33,36,.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(2px)',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 7, width: '100%', maxWidth: 480,
        margin: 16, boxShadow: '0 24px 38px rgba(0,0,0,.14), 0 9px 46px rgba(0,0,0,.12)',
        animation: 'dialogIn .2s cubic-bezier(.2,0,0,1)',
        overflow: 'hidden',
      }}>
        <div style={{ padding: '10px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background:"var(--secondary-color)", color: "var(--primary-color)" }}>
          <h4>{title}</h4>
          <IconBtn iconPath={ICONS.close} label="Close" onClick={onClose} tint="#5F6368" />
        </div>
        <div style={{ padding: '16px 24px' }}>{children}</div>
        {actions && <div style={{ padding: '8px 16px 20px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>{actions}</div>}
      </div>
    </div>
  );
};

// ── Form Field ────────────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontFamily: "'Google Sans Text', sans-serif", fontSize: 12, fontWeight: 600, color: '#5F6368', letterSpacing: '.4px', textTransform: 'uppercase', marginBottom: 6,borderRadius:5 }}>{label}</label>
    {children}
  </div>
);

const Select = ({ value, onChange, children }) => (
  <select value={value} onChange={onChange} style={{
    width: '100%', height: 48, borderRadius: 12,
    border: '1.5px solid #DADCE0', background: '#F8F9FA',
    fontFamily: "'Google Sans Text', sans-serif", fontSize: 14, color: '#202124',
    padding: '0 12px', outline: 'none', cursor: 'pointer',
    appearance: 'auto',
  }}
  onFocus={e => e.target.style.borderColor = '#1A73E8'}
  onBlur={e => e.target.style.borderColor = '#DADCE0'}
  >{children}</select>
);

const TextArea = ({ value, onChange, placeholder }) => (
  <textarea value={value} onChange={onChange} placeholder={placeholder} rows={3} style={{
    width: '100%', borderRadius: 12, border: '1.5px solid #DADCE0', background: '#F8F9FA',
    fontFamily: "'Google Sans Text', sans-serif", fontSize: 14, color: '#202124',
    padding: '12px', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
  }}
  onFocus={e => e.target.style.borderColor = '#1A73E8'}
  onBlur={e => e.target.style.borderColor = '#DADCE0'}
  />
);

// ── Toast ─────────────────────────────────────────────────────────────────────
// Using react-toastify for toast notifications

// ── Filter Tab ────────────────────────────────────────────────────────────────
const FilterTab = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontFamily: "'Google Sans Text', sans-serif", fontWeight: 600, fontSize: 13,
    background: active ? '#E8F0FE' : 'transparent',
    color: active ? '#1A73E8' : '#5F6368',
    transition: 'all .15s',
  }}
  onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#F1F3F4' }}
  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
  >{label}</button>
);

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ name, bg = '#1A73E8' }) => (
  <div style={{
    width: 32, height: 32, borderRadius: '50%',
    background: bg, color: '#fff', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontFamily: "'Google Sans Text', sans-serif", fontWeight: 700,
  }}>{name?.charAt(0)?.toUpperCase()}</div>
);

const AVATAR_COLORS = ['#1A73E8','#34A853','#FBBC04','#EA4335','#9334E6','#00ACC1','#E67C10'];

// ── Main Component ────────────────────────────────────────────────────────────
export default function BookRequestManager() {
  // ── Data State ──────────────────────────────────────────────────────────────
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [stats, setStats]         = useState({ pending_count: 0, fulfilled_count: 0, cancelled_count: 0, total_count: 0 });

  // ── Filter / Search ─────────────────────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState('PENDING');
  const [searchTerm, setSearchTerm]     = useState('');

  // ── Modals ──────────────────────────────────────────────────────────────────
  const [openCreate, setOpenCreate] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [openAvail,  setOpenAvail]  = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // ── Form State ──────────────────────────────────────────────────────────────
  const [createForm, setCreateForm]           = useState({ book_id: '', member_id: '' });
  const [statusForm, setStatusForm]           = useState({ status: 'PENDING', notes: '' });
  const [availableCopiesInfo, setAvailableCopiesInfo] = useState(null);
  const [books, setBooks]                     = useState([]);
  const [allBooks, setAllBooks]               = useState([]);
  const [members, setMembers]                 = useState([]);
  const [loadingBooks, setLoadingBooks]       = useState(false);


  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // ── Fetch Requests ──────────────────────────────────────────────────────────
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const api = new DataApi("bookrequest");
      const response = await api.get(`?status=${filterStatus}`);
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setRequests(data);
    } catch {
      toast.error('Failed to fetch book requests');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const api = new DataApi("bookrequest");
      const response = await api.get('/stats');
      setStats(response.data?.data || response.data);
    } catch (err) {
      console.error('Stats error:', err);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [fetchRequests, fetchStats]);

  // ── Filtered list (client-side search) ─────────────────────────────────────
  const filtered = useMemo(() =>
    requests.filter(r =>
      r.book_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.card_number?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [requests, searchTerm]);

  // ── Open Create Dialog & load books/members ─────────────────────────────────
  const handleOpenCreateDialog = async () => {
    setCreateForm({ book_id: '', member_id: '' });
    setBooks([]);
    setOpenCreate(true);
    setLoadingBooks(true);
    try {
      const apiB = new DataApi("bookrequest/unavailable-books");
      const apiM = new DataApi("librarycard");
      const [resB, resM] = await Promise.all([apiB.get(''), apiM.get('')]);
      
      setMembers(resM.data?.data || resM.data || []);
      setAllBooks(resB.data?.data || resB.data || []);
      
    } catch (err) {
      toast.error('Error loading form data');
    } finally {
      setLoadingBooks(false);
    }
  };

  // ── Filter books based on selected member's age ──────────────────────────────
  useEffect(() => {
    if (!createForm.member_id || !allBooks.length) {
      setBooks([]);
      return;
    }

    const selectedMember = members.find(m => m.id == createForm.member_id);
    if (!selectedMember || !selectedMember.dob) {
      setBooks([]);
      return;
    }

    // Calculate member age
    const calculateAge = (dob) => {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    };

    const memberAge = calculateAge(selectedMember.dob);
    console.log(`Selected Member: ${selectedMember.first_name || selectedMember.name}, Age: ${memberAge}`);

    // Filter books based on member age
    const filteredBooks = allBooks.filter(book => {
      const minAge = book.min_age || 0;
      const maxAge = book.max_age || 150;
      return memberAge >= minAge && memberAge <= maxAge;
    });

    console.log(`Filtered books for age ${memberAge}:`, filteredBooks);
    setBooks(filteredBooks);
  }, [createForm.member_id, allBooks, members]);

  // ── Check availability before creating ─────────────────────────────────────
  const handleProceedToCreate = async () => {
    if (!createForm.book_id || !createForm.member_id) {
      toast.warn('Please select both a book and a member');
      return;
    }
    try {
      const api = new DataApi("bookrequest");
      const response = await api.get(`/check-availability/book/${createForm.book_id}`);
      console.log('Availability response:', response.data);
      if (response.data && response.data.totalAvailable > 0) {
        setAvailableCopiesInfo(response.data);
        setOpenAvail(true);
      } else {
        await handleCreateRequest();
      }
    } catch {
      toast.error('Failed to check availability');
    }
  };

  // ── Create Request ──────────────────────────────────────────────────────────
  const handleCreateRequest = async () => {
    try {
      const api = new DataApi("bookrequest");
      await api.post('/create', createForm);
      toast.success('Request created successfully');
      setOpenCreate(false);
      setOpenAvail(false);
      fetchRequests();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Creation failed');
    }
  };

  // ── Update Status ───────────────────────────────────────────────────────────
  const handleUpdateStatus = async () => {
    try {
      const api = new DataApi("bookrequest");
      await api.post(`/${selectedRequest.id}/status`, statusForm);
      toast.success('Status updated');
      setOpenStatus(false);
      fetchRequests();
      fetchStats();
    } catch {
      toast.error('Update failed');
    }
  };

  // ── Send Notification ───────────────────────────────────────────────────────
  const handleSendNotification = async (requestId) => {
    toast.info('Sending notification…');
    try {
      const api = new DataApi("bookrequest");
      await api.post(`/${requestId}/notify`);
      toast.success('Email sent successfully');
    } catch {
      toast.error('Failed to notify member');
    }
  };


  
  // ── Delete ──────────────────────────────────────────────────────────────────

  const confirmDelete = async () => {
  try {
    const api = new DataApi("bookrequest");
    await api.delete(deleteId);

    toast.info('Request deleted');
    fetchRequests();
    fetchStats();
  } catch {
    toast.error('Deletion failed');
  } finally {
    setShowConfirm(false);
    setDeleteId(null);
  }
};



  const handleDeleteRequest = (requestId) => {
  if (!requestId) return;

  setDeleteId(requestId);
  setShowConfirm(true); // open modal
};


  return (
    <>
    <ConfirmationModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Request"
        message="Are you sure you want to delete this request? This action cannot be undone."
    />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Google+Sans+Display:wght@400;500;700&family=Google+Sans+Text:wght@400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes dialogIn {
          from { opacity: 0; transform: scale(.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #DADCE0; border-radius: 4px; }
        .row-hover:hover { background: #F8F9FA !important; }
      `}</style>

      <div style={{
        minHeight: '100vh', background: '#F8F9FA',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        <div style={{ margin: '0 auto', padding: '32px 24px' }}>

          {/* ── Header ──────────────────────────────────────── */}
          <div style={{ marginBottom: 32 , background: 'var(--secondary-color)', color: 'var(--primary-color)', padding: "5px 12px" , borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon d={ICONS.book} size={22} color="#fff" />
              </div>
              <h4 style={{color:"var(--primary-color)", fontWeight:"600",marginTop:"5px"}}>Book Requests</h4>
            </div>
            {/* <p style={{ color: '#5F6368', fontSize: 14, marginLeft: 52 }}>Manage patron book requests and fulfillment</p> */}
          </div>

          {/* ── Stat Cards ───────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
            <StatCard label="Pending"   value={stats.pending_count}   iconPath={ICONS.pending} primaryColor="#F9AB00" surfaceColor="#fff" />
            <StatCard label="Fulfilled" value={stats.fulfilled_count} iconPath={ICONS.check}   primaryColor="#34A853" surfaceColor="#fff" />
            <StatCard label="Cancelled" value={stats.cancelled_count} iconPath={ICONS.close}   primaryColor="#EA4335" surfaceColor="#fff" />
            <StatCard label="All Time"  value={stats.total_count}     iconPath={ICONS.book}    primaryColor="#1A73E8" surfaceColor="#fff" />
          </div>


          {/* ── Toolbar ─────────────────────────────────────── */}
          <div style={{
            background: '#fff', borderRadius: 5,
            padding: '12px 16px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            boxShadow: '0 1px 3px rgba(0,0,0,.08)',
          }}>
            {/* Search */}
            <div style={{
              flex: '1 1 240px', display: 'flex', alignItems: 'center', gap: 8,
              background: '#F1F3F4', borderRadius: 6, padding: '9px 16px',
            }}>
              <Icon d={ICONS.search} size={18} color="#5F6368" />
              <input
                placeholder="Search requests…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  border: 'none', background: 'transparent', outline: 'none',
                  fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#202124', width: '100%',
                }}
              />
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 4, background: '#F1F3F4', borderRadius: 6, padding: 2 }}>
              {['PENDING','FULFILLED','CANCELLED'].map(s => (
                <FilterTab key={s} label={STATUS_CONFIG[s].label} active={filterStatus === s} onClick={() => setFilterStatus(s)} />
              ))}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              {/* <IconBtn iconPath={ICONS.refresh} label="Refresh" onClick={() => { fetchRequests(); fetchStats(); }} tint="#5F6368"
                style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}
              /> */}
              <FilledBtn onClick={handleOpenCreateDialog}  style={{background:"var(--primary-color)", color:"#fff"}}>New Request</FilledBtn>
            </div>
          </div>

          {/* ── Table Card ──────────────────────────────────── */}
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E8EAED' }}>
                  {['Book', 'Member', 'Date', 'Status', 'Actions'].map((h, i) => (
                    <th key={i} style={{
                      padding: '14px 16px', textAlign: i === 4 ? 'right' : 'left',
                      fontFamily: "'Google Sans Text', sans-serif", fontWeight: 600,
                      fontSize: 11, color: '#5F6368', letterSpacing: '.8px', textTransform: 'uppercase',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '64px 24px' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #E8F0FE', borderTopColor: '#1A73E8', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                      <div style={{ color: '#9AA0A6', fontFamily: "'Google Sans Text', sans-serif", fontSize: 14 }}>Loading requests…</div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '64px 24px', color: '#9AA0A6' }}>
                      <Icon d={ICONS.info} size={48} color="#DADCE0" />
                      <div style={{ fontFamily: "'Google Sans Display', sans-serif", fontSize: 18, fontWeight: 600, marginTop: 12, color: '#5F6368' }}>No requests found</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}>Try changing filters or add a new request</div>
                    </td>
                  </tr>
                ) : filtered.map((req, idx) => (
                  <tr key={req.id} className="row-hover" style={{ borderBottom: idx < filtered.length - 1 ? '1px solid #F1F3F4' : 'none', transition: 'background .1s' }}>
                    {/* Book */}
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#E8F0FE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon d={ICONS.book} size={20} color="#1A73E8" />
                        </div>
                        <div>
                            {/* need to linkable negavative to book details page */}
                            <a href={`/book/${req.book_id}`} style={{ textDecoration: 'none' }}>
                              <div style={{ fontWeight: 600, fontSize: 14, color: '#6b87dc' }}>{req.book_title}</div>
                            </a>
                          <div style={{ fontSize: 11, color: '#9AA0A6', fontFamily: "'DM Mono', monospace", marginTop: 2 }}>ISBN {req.book_isbn || '—'}</div>
                        </div>
                      </div>
                    </td>
                    {/* Member */}
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={req.member_name} bg={AVATAR_COLORS[req.id % AVATAR_COLORS.length]} />
                        <div>
                            {/* need to linkable negavatve to member details page */}
                            <a href={`/librarycard/${req.member_id}`} style={{ textDecoration: 'none' }}>
                              <div style={{ fontWeight: 500, fontSize: 14, color: '#1A73E8' }}>{req.member_name}</div>
                            </a>
                          <div style={{ fontSize: 11, color: '#9AA0A6', fontFamily: "'DM Mono', monospace", marginTop: 1 }}>{req.card_number}</div>
                        </div>
                      </div>
                    </td>
                    {/* Date */}
                    <td style={{ padding: '14px 16px', fontSize: 13, color: '#5F6368', whiteSpace: 'nowrap' }}>
                      {new Date(req.request_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    {/* Status */}
                    <td style={{ padding: '14px 16px' }}><StatusChip status={req.status} /></td>
                    {/* Actions */}
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <IconBtn iconPath={ICONS.mail}  label="Notify via Email"  tint="#1A73E8" disabled={!req.member_email} onClick={() => handleSendNotification(req.id)} />
                        <IconBtn iconPath={ICONS.edit}  label="Change Status"     tint="#00ACC1" onClick={() => { setSelectedRequest(req); setStatusForm({ status: req.status, notes: req.notes || '' }); setOpenStatus(true); }} />
                        <IconBtn iconPath={ICONS.trash} label="Delete Request"    tint="#EA4335" onClick={() => handleDeleteRequest(req.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > 0 && (
              <div style={{ padding: '12px 16px', borderTop: '1px solid #F1F3F4', color: '#9AA0A6', fontSize: 12, fontFamily: "'Google Sans Text', sans-serif" }}>
                Showing {filtered.length} of {requests.length} requests
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Create Dialog ──────────────────────────────────── */}
      <Dialog backdrop='static' open={openCreate} onClose={() => setOpenCreate(false)} title="New Book Request"
        actions={<>
          <FilledBtn variant="outlined" onClick={() => setOpenCreate(false)}>Cancel</FilledBtn>
          <FilledBtn onClick={handleProceedToCreate} >
            {loadingBooks ? 'Loading…' : 'Create Request'}
          </FilledBtn>
        </>}
      >
        <Field label="Member">
          <Select value={createForm.member_id} onChange={e => setCreateForm(p => ({...p, member_id: e.target.value, book_id: ''}))}>
            <option value="">{loadingBooks ? 'Loading members…' : 'Select a member…'}</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.first_name || m.name} ({m.card_number})</option>)}
          </Select>
        </Field>
        <Field label="Book" className="boder-radius:5px;">
          <Select value={createForm.book_id} onChange={e => setCreateForm(p => ({...p, book_id: e.target.value}))}>
            <option value="">{loadingBooks ? 'Loading books…' : 'Select a book…'}</option>
            {books.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
          </Select>
        </Field>
      </Dialog>

      {/* ── Status Dialog ──────────────────────────────────── */}
      <Dialog open={openStatus} onClose={() => setOpenStatus(false)} title="Update Status"
        actions={<>
          <FilledBtn variant="outlined" onClick={() => setOpenStatus(false)}>Cancel</FilledBtn>
          <FilledBtn onClick={handleUpdateStatus}>Save Changes</FilledBtn>
        </>}
      >
        {selectedRequest && (
          <div style={{ background: '#F8F9FA', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#E8F0FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon d={ICONS.book} size={18} color="#1A73E8" />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#202124' }}>{selectedRequest.book_title}</div>
              <div style={{ fontSize: 12, color: '#5F6368' }}>{selectedRequest.member_name}</div>
            </div>
          </div>
        )}
        <Field label="New Status">
          <Select value={statusForm.status} onChange={e => setStatusForm(p => ({...p, status: e.target.value}))}>
            <option value="PENDING">Pending</option>
            <option value="FULFILLED">Fulfilled</option>
            <option value="CANCELLED">Cancelled</option>
          </Select>
        </Field>
        <Field label="Internal Notes">
          <TextArea value={statusForm.notes} onChange={e => setStatusForm(p => ({...p, notes: e.target.value}))} placeholder="Add any internal notes…" />
        </Field>
      </Dialog>

      {/* ── Availability Alert Dialog ──────────────────────── */}
      <Dialog open={openAvail} onClose={() => setOpenAvail(false)} title="Copies Available">
        <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
          <div style={{ width: 64, height: 64, borderRadius: 32, background: '#FEF7E0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Icon d={ICONS.warn} size={32} color="#F9AB00" />
          </div>
          <p style={{ fontSize: 14, color: '#5F6368', lineHeight: 1.6 }}>
            We found <strong style={{ color: '#202124' }}>{availableCopiesInfo?.totalAvailable}</strong> copy/copies currently in stock.<br />
            You could issue this book directly instead of creating a pending request.
          </p>
          {availableCopiesInfo?.availableCopies?.length > 0 && (
            <div style={{ background: '#F8F9FA', borderRadius: 12, padding: '12px 16px', textAlign: 'left', marginTop: 16, fontSize: 13 }}>
              <div style={{ fontWeight: 600, color: '#202124', marginBottom: 8, fontFamily: "'Google Sans Text', sans-serif" }}>Available copies</div>
              {availableCopiesInfo.availableCopies.map(c => (
                <div key={c.id} style={{ color: '#5F6368', padding: '4px 0', borderBottom: '1px solid #EEEEEE', fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
                  • {c.barcode} ({c.call_number})
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <FilledBtn onClick={handleCreateRequest}>Continue & Request Anyway</FilledBtn>
          <FilledBtn variant="tonal" onClick={() => setOpenAvail(false)}>Cancel & Issue Directly</FilledBtn>
        </div>
      </Dialog>

      <ToastContainer
        position="top-right"
        autoClose={3500}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}